import { OrganizationRiskMatrix, DEFAULT_RISK_MATRIX, Criticality, getCriticalityValue, getRiskLevel } from '@easydone/shared';
import { StarIcon } from '@heroicons/react/24/solid';

interface RiskMatrixGridProps {
  riskMatrix?: OrganizationRiskMatrix;
  /** If provided, shows a star at this FLOC's position */
  highlightedFloc?: {
    criticality: Criticality;
    conditionScore: number;
  };
  className?: string;
}

export function RiskMatrixGrid({ riskMatrix = DEFAULT_RISK_MATRIX, highlightedFloc, className = '' }: RiskMatrixGridProps) {
  const criticalityLevels: { label: string; value: Criticality; numericValue: number }[] = [
    { label: 'Very high', value: 'very_high', numericValue: 5 },
    { label: 'High', value: 'high', numericValue: 4 },
    { label: 'Medium', value: 'medium', numericValue: 3 },
    { label: 'Low', value: 'low', numericValue: 2 },
    { label: 'Very low', value: 'very_low', numericValue: 1 },
    { label: 'Not in use', value: 'not_in_use', numericValue: 0 },
  ];

  const conditionScores = [
    { score: 1, label: 'Very good' },
    { score: 2, label: 'Good' },
    { score: 3, label: 'Moderate' },
    { score: 4, label: 'Poor' },
    { score: 5, label: 'Very poor' },
  ];

  const getRiskScore = (criticalityValue: number, conditionScore: number): number => {
    return criticalityValue * conditionScore;
  };

  const getCellColor = (riskScore: number): string => {
    const level = getRiskLevel(riskScore, riskMatrix);

    switch (level) {
      case 'low':
        return 'bg-green-200 border-green-400';
      case 'medium':
        return 'bg-yellow-200 border-yellow-400';
      case 'high':
        return 'bg-orange-200 border-orange-400';
      case 'critical':
        return 'bg-red-200 border-red-400';
    }
  };

  const isHighlightedCell = (criticalityValue: number, conditionScore: number): boolean => {
    if (!highlightedFloc) return false;
    const flocCriticalityValue = getCriticalityValue(highlightedFloc.criticality);
    return flocCriticalityValue === criticalityValue && highlightedFloc.conditionScore === conditionScore;
  };

  return (
    <div className={`overflow-x-auto ${className}`}>
      <div className="inline-block min-w-full align-middle">
        <table className="border-collapse border-2 border-gray-400">
          <thead>
            <tr>
              <th className="border-2 border-gray-400 bg-gray-100 px-3 py-2 text-xs font-semibold text-gray-700">
                Criticality ↓<br />Condition →
              </th>
              {conditionScores.map((condition) => (
                <th
                  key={condition.score}
                  className="border-2 border-gray-400 bg-gray-100 px-3 py-2 text-xs font-semibold text-gray-700 text-center min-w-[80px]"
                >
                  <div>{condition.score}</div>
                  <div className="font-normal text-[10px] text-gray-600 mt-0.5">
                    {condition.label}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {criticalityLevels.map((criticality) => (
              <tr key={criticality.value}>
                <td className="border-2 border-gray-400 bg-gray-100 px-3 py-2 text-xs font-semibold text-gray-700 whitespace-nowrap">
                  {criticality.label} ({criticality.numericValue})
                </td>
                {conditionScores.map((condition) => {
                  const riskScore = getRiskScore(criticality.numericValue, condition.score);
                  const cellColor = getCellColor(riskScore);
                  const isHighlighted = isHighlightedCell(criticality.numericValue, condition.score);

                  return (
                    <td
                      key={`${criticality.value}-${condition.score}`}
                      className={`border-2 ${cellColor} px-3 py-3 text-center relative`}
                    >
                      <div className="flex flex-col items-center justify-center min-h-[50px]">
                        {isHighlighted && (
                          <StarIcon className="w-8 h-8 text-blue-600 mb-1" />
                        )}
                        <div className="text-lg font-bold text-gray-900">
                          {riskScore}
                        </div>
                        {isHighlighted && (
                          <div className="text-[10px] font-semibold text-blue-800 mt-1">
                            Current
                          </div>
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-green-200 border-2 border-green-400 rounded"></div>
          <div className="text-xs">
            <div className="font-semibold text-gray-900">Low (1-{riskMatrix.thresholds.low.max})</div>
            <div className="text-gray-600">{riskMatrix.thresholds.low.label}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-yellow-200 border-2 border-yellow-400 rounded"></div>
          <div className="text-xs">
            <div className="font-semibold text-gray-900">
              Medium ({riskMatrix.thresholds.low.max + 1}-{riskMatrix.thresholds.medium.max})
            </div>
            <div className="text-gray-600">{riskMatrix.thresholds.medium.label}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-orange-200 border-2 border-orange-400 rounded"></div>
          <div className="text-xs">
            <div className="font-semibold text-gray-900">
              High ({riskMatrix.thresholds.medium.max + 1}-{riskMatrix.thresholds.high.max})
            </div>
            <div className="text-gray-600">{riskMatrix.thresholds.high.label}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-red-200 border-2 border-red-400 rounded"></div>
          <div className="text-xs">
            <div className="font-semibold text-gray-900">
              Critical ({riskMatrix.thresholds.high.max + 1}-{riskMatrix.thresholds.critical.max})
            </div>
            <div className="text-gray-600">{riskMatrix.thresholds.critical.label}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
